import {
  Backdrop,
  CircularProgress,
  Typography,
  Divider,
  Grid,
  ButtonGroup,
  Button,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { format } from "date-fns";
import { styled } from "@mui/material/styles";
import { Document, Page } from "react-pdf";
import * as pdfjs from "pdfjs-dist";
import { docsResources } from "./.resources";
import { useResources } from "../../hooks/useResources";
import { SizeMe } from "react-sizeme";
import ReactMarkdown from "react-markdown";
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

interface IWikiRendererProps {
  title?: string;
  url?: string;
  date?: Date;
  data?: string;
  isLoading: boolean;
}

const WikiRenderer: React.FC<IWikiRendererProps> = ({
  url,
  date,
  data,
  isLoading,
}) => {
  const [hasContent, setHasContent] = useState<boolean>(false);
  const [isPdf, setIsPdf] = useState<boolean>(false);
  const [isMarkdown, setIsMarkdown] = useState<boolean>(false);
  const [, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pdfPages, setPdfPages] = useState<number[]>([]);

  // Language sync is handled globally in App.tsx
  const res = useResources(docsResources);

  useEffect(() => {
    setHasContent(Boolean(data));
  }, [data]);

  useEffect(() => {
    setIsMarkdown(false);
    setIsPdf(false);
    if (url) {
      const extension = url.split(".").pop();
      if (extension === "pdf") {
        setIsPdf(true);
      } else if (extension === "md") {
        setIsMarkdown(true);
      }
    }
    setHasContent(Boolean(data));
  }, [url]);

  function onDocumentLoadSuccess({ numPages }) {
    setNumPages(numPages);
    setPdfPages(Array.from(Array(numPages).keys()));
  }

  const StyledBackdrop = styled(Backdrop)(({ theme }) => ({
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
  }));

  const StyledDivider = styled("div")(() => ({
    margin: "32px",
  }));

  return (
    <>
      {hasContent && (
        <>
          <Typography variant="overline">
{date ? format(new Date(date), "dd/MMM/yyyy") : ""}
          </Typography>

          <Divider variant="middle" />
        </>
      )}

      <StyledDivider />
      <StyledBackdrop open={isLoading}>
        <CircularProgress color="inherit" />
      </StyledBackdrop>
      {isLoading === false && isMarkdown && <ReactMarkdown>{data}</ReactMarkdown>}
      {isLoading === false && isPdf && (
        <SizeMe>
          {({ size }) => (
            <>
              <div style={{ border: "1px solid black " }}>
                <Document file={url} onLoadSuccess={onDocumentLoadSuccess}>
                  <Page
                    pageNumber={pageNumber}
                    width={size.width ? size.width - 64 : 1}
                  />
                </Document>
              </div>
              <Grid container>
                <Grid>
                  <Typography variant="subtitle1">{res.page}</Typography>
                  <ButtonGroup variant="text" size="small">
                    {pdfPages.map((n) => {
                      return (
                        <Button
                          key={n + 1}
                          onClick={() => setPageNumber(n + 1)}
                          variant={n === pageNumber - 1 ? "contained" : "text"}
                          color={n === pageNumber - 1 ? "primary" : "inherit"}
                        >
                          {n + 1}
                        </Button>
                      );
                    })}
                  </ButtonGroup>
                </Grid>
              </Grid>
            </>
          )}
        </SizeMe>
      )}
    </>
  );
};

export default WikiRenderer;
